import React, { useState, useEffect } from 'react';
import Template from '../Components/Template';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Import the styles
import {
  useAddProductMutation,
  useDeleteProductMutation,
  useGetAllProductsQuery,
  useUpdateProductMutation,
} from '../Services/modules/products';
import {
  MdOutlineNavigateNext,
  MdSkipNext,
  MdSkipPrevious,
  MdEdit,
  MdDelete,
} from 'react-icons/md';
import { GrFormPrevious } from 'react-icons/gr';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { updateProductData } from '../Store/ProductData';
import WarningModal from '../Components/WarningModal';

function Products() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
 const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const productCategoryId = queryParams.get('categoryId');
  const categoryName = queryParams.get('productCategoryName');
  // ---------- Client-side pagination state ----------
  const rowsPerPage = 10; // fixed to 10 as requested
  const [page, setPage] = useState(0);
  const sequenceStartIndex = page * rowsPerPage + 1;

  // ---------- Local UI state ----------
  const [allProducts, setProducts] = useState([]);
  const [isSumbmiting, setIsSubmitting] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);

  // ---------- Mutations & Query ----------
  const [AddProduct, { data, isSuccess, isLoading, isError, error }] =
    useAddProductMutation();

  // Fetch ALL products once; we will slice on the client
  const {
    data: dataProduct,
    isSuccess: isSuccessProduct,
    isLoading: isLoadingProduct,
    error: errorProduct,
    refetch,
  } = useGetAllProductsQuery({category_id:productCategoryId}); // <-- no page params (fetch all)

  const [
    UpdateProduct,
    {
      data: updateData,
      isSuccess: updateIsSuccess,
      isLoading: updateIsLoading,
      isError: updateIsError,
      error: updateError,
    },
  ] = useUpdateProductMutation();

  const [
    DeleteProduct,
    {
      data: deleteData,
      isSuccess: deleteIsSuccess,
      isLoading: deleteIsLoading,
      isError: deleteIsError,
      error: deleteError,
    },
  ] = useDeleteProductMutation();

  const [product_data, setProductData] = useState({
    categoryId:productCategoryId,
    product_id: '',
    product_name: '',
    product_introduction: '',
    product_benefits: '',
    packing_details: '',
    recommended_crops: '',
    recommended_dose: '',
    product_link: '',
    recommended_dripping: '',
    disclaimer: '',
    caution: '',
    is_homepage: false,
  });

  function handleChange(name, value) {
    setProductData({
      ...product_data,
      [name]: value,
    });
  }

  const handleQuillChange = (content) => {
    handleChange('product_benefits', content);
  };

  const clearProductData = () => {
    setProductData({
      ...product_data,
      product_id: '',
      product_name: '',
      product_introduction: '',
      product_benefits: '',
      packing_details: '',
      recommended_crops: '',
      recommended_dose: '',
      is_homepage: false,
      product_link: '',
      recommended_dripping: '',
      disclaimer: '',
      caution: '',
    });
  };

  const onSubmit = () => {
    setIsSubmitting(true);
    if (!isUpdate) {
      AddProduct(product_data);
    } else {
      UpdateProduct(product_data);
    }
  };

  const handleProductEdit = (dataRow) => {
    setProductData({
      ...product_data,
      product_id: dataRow._id,
      product_name: dataRow.product_name,
      product_introduction: dataRow.product_introduction,
      product_benefits: dataRow.product_benefits,
      packing_details: dataRow.packing_details,
      recommended_crops: dataRow.recommended_crops,
      recommended_dose: dataRow.recommended_dose,
      is_homepage: dataRow.is_homepage,
      product_link: dataRow.product_link,
      recommended_dripping: dataRow.recommended_dripping,
      disclaimer: dataRow.disclaimer,
      caution: dataRow.caution,
    });
    setIsUpdate(true);
  };

  const handleProductDelete = (dataRow) => {
    setDeleteModal(true);
    setProductData({
      ...product_data,
      product_id: dataRow._id,
    });
  };

  // ---------- Effects ----------
  // Load products
  useEffect(() => {
    if (isSuccessProduct && dataProduct) {
      // Assuming your API returns { data: [...] }
      const list = Array.isArray(dataProduct?.data) ? dataProduct.data : [];
      console.log("listttt",list)
      setProducts(list);
    }
  }, [isSuccessProduct, dataProduct]);

  // After add success
  useEffect(() => {
    if (isSuccess) {
      setIsSubmitting(false);
      toast.success(data?.message || 'Product added');
      clearProductData();
      refetch();
    }
  }, [isSuccess, data, refetch]);

  // After update success
  useEffect(() => {
    if (updateIsSuccess) {
      if (!updateData?.error) {
        toast.success(updateData?.message || 'Product updated');
        setIsUpdate(false);
        clearProductData();
        setIsSubmitting(false);
        refetch();
      } else {
        toast.error(updateData?.message || 'Update failed');
        clearProductData();
      }
    }
  }, [updateIsSuccess, updateData, refetch]);

  // After delete success
  useEffect(() => {
    if (deleteIsSuccess) {
      if (!deleteData?.error) {
        toast.success(deleteData?.message || 'Product deleted');
        setIsDelete(false);
        setDeleteModal(false);
        clearProductData();
        refetch();
      } else {
        toast.error(deleteData?.message || 'Delete failed');
        setIsDelete(false);
        setDeleteModal(false);
      }
    }
  }, [deleteIsSuccess, deleteData, refetch]);

  // Trigger delete when confirmed in modal
  useEffect(() => {
    if (isDelete) {
      DeleteProduct(product_data);
    }
  }, [isDelete]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep page in range when data size changes (e.g., after delete)
  const totalPages = Math.max(1, Math.ceil(allProducts.length / rowsPerPage));
  useEffect(() => {
    if (page > totalPages - 1) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [allProducts.length, totalPages, page]);

  // ---------- Client-side sliced data ----------
  const pagedProducts = allProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // ---------- Pagination handlers ----------
  const handleFirstPage = () => setPage(0);
  const handlePrevPage = () => setPage((p) => Math.max(0, p - 1));
  const handleNextPage = () =>
    setPage((p) => Math.min(totalPages - 1, p + 1));
  const handleLastPage = () => setPage(totalPages - 1);

  const navigateToProductDetails = (productDetails) => {
    dispatch(updateProductData({ data: productDetails }));
    navigate({
      pathname: '/productdetails',
    });
  };

  const productScreen = () => {
    return (
      <>
        <ToastContainer />

        <WarningModal
          setShowWarningModal={setDeleteModal}
          modalTitle={'Delete Product'}
          setIsDelete={setIsDelete}
          showModal={isDeleteModal}
        />

        <main id="main" className="main">
          <div className="container">
            <div className="row">
              {/* Left: Add / Edit form */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
              <h5 className="card-title">Add Product Details for category- {categoryName}</h5>

                    <h5 className="card-title">
                      {isUpdate ? 'Edit Product Details' : 'Add Product Details'}
                    </h5>

                    <div className="form-group">
                      <label htmlFor="input1">Product Name</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="input1"
                        name="product_name"
                        value={product_data.product_name}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="input3">Product Introduction</label>
                      <textarea
                        className="form-control mb-3"
                        id="product_introduction"
                        name="product_introduction"
                        value={product_data.product_introduction}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                        rows="5"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="input3">Product Benefits</label>
                      <textarea
                        className="form-control mb-3"
                        id="product_benefits"
                        name="product_benefits"
                        value={product_data.product_benefits}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                        rows="5"
                      />
                      {/* Or use Quill:
                      <ReactQuill
                        className="mb-3"
                        value={product_data.product_benefits}
                        onChange={handleQuillChange}
                      /> */}
                    </div>

                    <div className="form-group">
                      <label htmlFor="packing_details">Packing Details</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="packing_details"
                        name="packing_details"
                        value={product_data.packing_details}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="recommended_dose">Dosage</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="recommended_dose"
                        name="recommended_dose"
                        value={product_data.recommended_dose}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="recommended_dripping">Dripping</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="recommended_dripping"
                        name="recommended_dripping"
                        value={product_data.recommended_dripping}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="disclaimer">Disclaimer</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="disclaimer"
                        name="disclaimer"
                        value={product_data.disclaimer}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="caution">Caution</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="caution"
                        name="caution"
                        value={product_data.caution}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="recommended_crops">Crops</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="recommended_crops"
                        name="recommended_crops"
                        value={product_data.recommended_crops}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="product_link">Product link</label>
                      <input
                        type="text"
                        className="form-control mb-3"
                        id="product_link"
                        name="product_link"
                        value={product_data.product_link}
                        onChange={(e) => handleChange(e.target.name, e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <input
                          className="mb-3"
                          name="is_homepage"
                          type="checkbox"
                          checked={product_data.is_homepage}
                          onChange={(e) =>
                            handleChange(e.target.name, e.target.checked)
                          }
                        />{' '}
                        Show Product On Homepage
                      </label>
                    </div>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      onClick={onSubmit}
                      disabled={isLoading || updateIsLoading || deleteIsLoading}
                    >
                      {isSumbmiting
                        ? 'Submitting...'
                        : isUpdate
                        ? 'Update'
                        : 'Submit'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Right: Table with client-side pagination */}
              <div className="col-md-6">
                <div className="card">
                  <div className="card-body">
                    <table className="bordered-table">
                      <thead>
                        <tr>
                          <th>Action</th>
                          <th>Index</th>
                          <th>Product Name</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedProducts.map((item, index) => (
                          <tr key={item._id || index}>
                            <td>
                              <MdEdit
                                onClick={() => handleProductEdit(item)}
                                style={{ cursor: 'pointer', marginRight: 8 }}
                                title="Edit"
                              />
                              <MdDelete
                                onClick={() => handleProductDelete(item)}
                                style={{ cursor: 'pointer' }}
                                title="Delete"
                              />
                            </td>
                            <td>{sequenceStartIndex + index}</td>
                            <td>
                              <a
                                onClick={() => navigateToProductDetails(item)}
                                style={{ cursor: 'pointer', color: 'blue' }}
                              >
                                {item.product_name}
                              </a>
                            </td>
                          </tr>
                        ))}

                        {pagedProducts.length === 0 && (
                          <tr>
                            <td colSpan={3} style={{ textAlign: 'center' }}>
                              {isLoadingProduct ? 'Loading...' : 'No data'}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Pagination controls (client-side) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                      <button onClick={handleFirstPage} disabled={page === 0}>
                        <MdSkipPrevious />
                      </button>
                      <button onClick={handlePrevPage} disabled={page === 0}>
                        <GrFormPrevious />
                      </button>
                      <span style={{ margin: '0 8px' }}>
                        Page {totalPages === 0 ? 0 : page + 1} / {totalPages}
                      </span>
                      <button
                        onClick={handleNextPage}
                        disabled={page >= totalPages - 1}
                      >
                        <MdOutlineNavigateNext />
                      </button>
                      <button
                        onClick={handleLastPage}
                        disabled={page >= totalPages - 1}
                      >
                        <MdSkipNext />
                      </button>
                    </div>

                    {/* Fixed 10 per page as requested */}
                    <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
                      Showing {rowsPerPage} per page (client-side)
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </>
    );
  };

  return <Template>{productScreen()}</Template>;
}

export default Products;
